/**
 * InactivePositBanner.tsx
 * 
 * Banner shown to Posit owners when viewing their own inactive Posits.
 * Explains the situation and provides upgrade options.
 */

import React from "react";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { 
  AlertTriangle,
  Crown,
  Users,
  MessageSquare,
  ArrowRight
} from "lucide-react";
import { Link } from "react-router-dom";
import { useSubscription } from "@/hooks/useSubscription";
import PositPlusLogo from "@/components/shared/PositPlusLogo";
import { STRIPE_PRICE_IDS } from "@/constants/pricing";

interface InactivePositBannerProps {
  positTitle: string;
  pendingRequests?: number;
}

const InactivePositBanner: React.FC<InactivePositBannerProps> = ({
  positTitle,
  pendingRequests = 0
}) => {
  const { handleSubscribe } = useSubscription();

  const handleUpgrade = async () => {
    await handleSubscribe(STRIPE_PRICE_IDS.MONTHLY);
  };

  return (
    <Card className="mb-6 border-orange-200 bg-orange-50 dark:bg-orange-950 dark:border-orange-800">
      <CardContent className="p-6">
        <div className="flex items-start gap-4">
          <div className="flex-shrink-0">
            <div className="w-10 h-10 bg-orange-100 dark:bg-orange-900 rounded-full flex items-center justify-center">
              <AlertTriangle className="h-5 w-5 text-orange-600 dark:text-orange-400" />
            </div>
          </div>
          
          <div className="flex-1 space-y-3">
            <div>
              <div className="flex items-center gap-2 mb-2">
                <h3 className="font-semibold text-orange-900 dark:text-orange-100">
                  "{positTitle}" is Currently Inactive
                </h3>
                <Badge variant="secondary" className="bg-orange-200 text-orange-800">
                  Free Plan Limit
                </Badge>
              </div>
              <p className="text-sm text-orange-700 dark:text-orange-200">
                Your Posit was deactivated when you switched to the free plan. 
                Free accounts can only have 1 active Posit at a time.
              </p>
            </div>

            {pendingRequests > 0 && (
              <div className="bg-blue-50 dark:bg-blue-950 p-3 rounded-lg border border-blue-200 dark:border-blue-800">
                <div className="flex items-center gap-2 mb-1">
                  <Users className="h-4 w-4 text-blue-600 dark:text-blue-400" />
                  <span className="font-medium text-blue-900 dark:text-blue-100 text-sm">
                    {pendingRequests} {pendingRequests === 1 ? 'person wants' : 'people want'} access
                  </span>
                </div>
                <p className="text-xs text-blue-700 dark:text-blue-200">
                  People have requested reactivation of this Posit
                </p>
              </div>
            )}

            <div className="flex flex-col sm:flex-row gap-3">
              <Button 
                onClick={handleUpgrade}
                className="flex items-center gap-2"
              >
                <Crown className="h-4 w-4" />
                Upgrade to Posit Plus
                <ArrowRight className="h-4 w-4" />
              </Button>
              
              <Button variant="outline" asChild>
                <Link to="/dashboard/posits">
                  View All Posits
                </Link>
              </Button>

              {pendingRequests > 0 && (
                <Button variant="outline" asChild>
                  <Link to="/dashboard/reactivation-requests">
                    <MessageSquare className="h-4 w-4 mr-2" />
                    View Requests ({pendingRequests})
                  </Link>
                </Button>
              )}
            </div>

            <div className="bg-white/50 dark:bg-gray-800/50 p-3 rounded border">
              <p className="text-xs text-muted-foreground">
                <strong>What happens when you upgrade:</strong> All your Posits will be reactivated immediately, 
                and you can create up to 10 total Posits with better revenue sharing (90% vs 80%).
              </p>
            </div>
          </div>
        </div>
      </CardContent>
    </Card>
  );
};

export default InactivePositBanner; 